import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Ban, Check, Plus, Settings, X } from 'lucide-react';
import {
  Badge,
  Button,
  buttonStyles,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import type { ApiError } from '../lib/api';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import {
  adProblems,
  cleanAd,
  emptyAd,
  KeywordIdeas,
  KeywordsEditor,
  SearchAdEditor,
  SearchAdPreview,
  SearchSection,
  type KeywordValue,
  type SearchAdValue,
} from './google-ads-search';
import type {
  GadsAlert,
  GadsApproval,
  GadsBidding,
  GadsCampaign,
  GadsCampaignDetail,
  GadsChangeEvent,
  GadsEligibility,
  GadsKind,
  GadsOperation,
  GadsOperationKind,
  GadsOverview,
  GadsPrerequisites,
  GadsProductBox,
  GadsProductState,
  GadsRecommendation,
  GadsSettings,
  GadsStatus,
  ProductPage,
} from '../lib/types';

/**
 * Pazarlama → Reklam merkezi → Google Ads kampanyaları (F7-38, ADR-146). Performance Max ve Alışveriş kampanyaları
 * ürün bazlı yönetilir; Arama kampanyası (F7-40, ADR-148) duyarlı arama reklamı ve anahtar kelimelerle. Bu ekran Google'a doğrudan yazmaz: istek onaya veya kuyruğa girer, işçi Google Ads API ile
 * uygular ve geri okuyup doğrular. Para harcatabilecek işlemler onay + iki adımlı doğrulama ister; yeni kampanya
 * Google'da duraklatılmış oluşturulur.
 */

const G = 'admin.gads';
export const BIDDING_BY_KIND: Record<GadsKind, GadsBidding[]> = {
  pmax: ['max_conv_value', 'max_conversions'],
  shopping: ['max_conv_value', 'max_clicks'],
  search: ['max_conv_value', 'max_conversions', 'max_clicks'],
};
/** Para etkisi gösterilmeyen işlemler (bütçe ve harcama başlatmaz). */
const NO_MONEY = new Set<GadsOperationKind>(['pause', 'rename', 'ad_text', 'keywords', 'negatives']);
/** Sunucudaki hedefleme listesiyle aynı (ülke: Google coğrafi hedef sabiti; dil: vitrin dilleri). */
const COUNTRIES = [
  'TR',
  'DE',
  'GB',
  'AE',
  'US',
  'NL',
  'FR',
  'AT',
  'BE',
  'CH',
  'AZ',
  'SA',
  'QA',
  'KW',
  'BG',
  'GR',
  'IT',
  'ES',
  'SE',
  'DK',
];
const LANGUAGES = ['tr', 'en', 'de', 'ar'];

export const statusTone: Record<GadsStatus, Tone> = {
  draft: 'neutral',
  paused: 'amber',
  enabled: 'moss',
  removed: 'slate',
};
const opTone: Record<GadsOperation['status'], Tone> = {
  pending_approval: 'amber',
  queued: 'pine',
  applying: 'pine',
  applied: 'moss',
  failed: 'rose',
  rejected: 'rose',
  cancelled: 'neutral',
};
export const productTone: Record<GadsProductState, Tone> = {
  queued: 'neutral',
  sent: 'pine',
  eligible: 'moss',
  limited: 'amber',
  not_eligible: 'rose',
};

/** Aylık en fazla ≈ günlük × 30,4 (sunucudakiyle aynı tam sayı hesabı: yukarı yuvarlanır). */
export const monthlyMax = (daily: number) => Math.ceil((daily * 304) / 10);

function useDates() {
  const { locale } = useI18n();
  const tag = { tr: 'tr-TR', en: 'en-GB', de: 'de-DE', ar: 'ar' }[locale] ?? 'tr-TR';
  const dt = new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' });
  const d = new Intl.DateTimeFormat(tag, { dateStyle: 'medium' });
  return {
    at: (v: string | null) => (v ? dt.format(new Date(v)) : '—'),
    day: (v: string | null) => (v ? d.format(new Date(`${v}T12:00:00Z`)) : '—'),
  };
}

/** API hata anahtarı → metin (errors.* çevirisi yoksa genel ileti). */
function useErrorText() {
  const t = useT();
  return (key: string | null) => {
    if (!key) return '';
    const s = t(`errors.${key}`);
    return s === `errors.${key}` ? t('errors.google_ads_request_rejected') : s;
  };
}

function AlertList({ alerts, currency }: { alerts: GadsAlert[]; currency: string }) {
  const t = useT();
  const fmt = useMoney();
  if (!alerts.length) return null;
  return (
    <ul className="space-y-1">
      {alerts.map((a) => (
        <li key={a.code} className="flex items-start gap-2 text-sm text-ink">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden />
          <span>
            {t(`${G}.alerts.${a.code}`, {
              spent: fmt(a.spentMinor, currency),
              cap: 'capMinor' in a ? fmt(a.capMinor, currency) : '',
              budget: 'dailyBudgetMinor' in a ? fmt(a.dailyBudgetMinor, currency) : '',
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------ Liste sayfası

export function GoogleAdsCampaignsPage() {
  const t = useT();
  const { can, roleKey } = usePermissions();
  const fmt = useMoney();
  const overview = useQuery({
    queryKey: ['gads'],
    queryFn: () => api.get<GadsOverview>('/v1/admin/google-ads/campaigns'),
  });
  const approvals = useQuery({
    queryKey: ['gads-approvals'],
    queryFn: () => api.get<GadsApproval[]>('/v1/admin/google-ads/approvals'),
  });
  const [wizard, setWizard] = useState(false);
  const [settings, setSettings] = useState(false);
  const [idem] = useState(() => new IdempotencyKey());
  const stop = useApiMutation(
    () =>
      api.post<{ pausing: number; cancelled: number }>(
        '/v1/admin/google-ads/emergency-stop',
        { confirm: true },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gads'], ['gads-approvals']],
      success: (r) => t(`${G}.stopDone`, { pausing: r.pausing, cancelled: r.cancelled }),
    },
  );
  const o = overview.data;
  const currency = o?.settings.currency ?? 'TRY';
  return (
    <PageFrame
      helpKey="page.google_ads_campaigns"
      actions={
        <>
          <div className="flex items-center gap-1">
            <Link to="/reklam-merkezi" className={buttonStyles({ variant: 'secondary' })}>
              {t(`${G}.backToAds`)}
            </Link>
          </div>
          <div className="flex items-center gap-1">
            <Button onClick={() => setSettings(true)}>
              <Settings className="h-4 w-4" aria-hidden />
              {t(`${G}.settings`)}
            </Button>
            <HelpTip k="gads.settings" />
          </div>
          {can('marketing.ads.manage') && (
            <div className="flex items-center gap-1">
              <Button variant="primary" onClick={() => setWizard(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t(`${G}.newCampaign`)}
              </Button>
              <HelpTip k="gads.new_campaign" />
            </div>
          )}
          {roleKey === 'owner' && (
            <div className="flex items-center gap-1">
              <ConfirmButton
                size="md"
                message={t(`${G}.stopConfirm`)}
                onConfirm={() => stop.mutateAsync(undefined)}
              >
                <Ban className="h-4 w-4" aria-hidden />
                {t(`${G}.stopAll`)}
              </ConfirmButton>
              <HelpTip k="gads.stop_all" />
            </div>
          )}
        </>
      }
    >
      {overview.isLoading || !o ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-6">
          <section aria-labelledby="gads-month" className="space-y-3">
            <h2 id="gads-month" className="flex items-center gap-1 font-cond text-lg font-semibold text-ink">
              {t(`${G}.monthTitle`)} <HelpTip k="gads.month" />
            </h2>
            <dl className="grid gap-3 sm:grid-cols-4">
              {[
                { label: t(`${G}.monthSpent`), value: fmt(o.month.spentMinor, currency) },
                {
                  label: t(`${G}.monthlyCap`),
                  value:
                    o.settings.monthlyCapMinor === null
                      ? t(`${G}.capMissing`)
                      : fmt(o.settings.monthlyCapMinor, currency),
                },
                { label: t(`${G}.projected`), value: fmt(o.month.projectedMonthlyMinor, currency) },
                {
                  label: t(`${G}.apiUsage`),
                  value: t(`${G}.apiUsageValue`, {
                    used: o.usageToday,
                    limit: o.settings.dailyOperationLimit,
                  }),
                },
              ].map((s) => (
                <div key={s.label} className="rounded-md border border-line bg-surface px-3 py-2">
                  <dt className="text-xs text-ink-3">{s.label}</dt>
                  <dd className="mt-0.5 text-[0.9375rem] font-medium tabular">{s.value}</dd>
                </div>
              ))}
            </dl>
            <p className="text-sm text-ink-3">{t(`${G}.projectedNote`)}</p>
            <AlertList alerts={o.month.alerts} currency={currency} />
          </section>

          <Approvals items={approvals.data ?? []} loading={approvals.isLoading} />

          <Section title={t(`${G}.campaignsTitle`)} action={<HelpTip k="gads.campaigns" />}>
            {!o.campaigns.length ? (
              <EmptyState title={t(`${G}.empty`)} description={t(`${G}.emptyLead`)} />
            ) : (
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[48rem] text-sm" aria-label={t(`${G}.campaignsTitle`)}>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t(`${G}.campaign`)}</th>
                      <th className="px-3 py-2 text-start font-medium">{t(`${G}.status`)}</th>
                      <th className="px-3 py-2 text-end font-medium">{t(`${G}.dailyBudget`)}</th>
                      <th className="px-3 py-2 text-end font-medium">{t(`${G}.monthlyMax`)}</th>
                      <th className="px-3 py-2 text-end font-medium">{t(`${G}.monthSpent`)}</th>
                      <th className="px-3 py-2 text-end font-medium">{t(`${G}.productsCount`)}</th>
                      <th className="px-3 py-2 text-start font-medium">{t(`${G}.notes`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.campaigns.map((c) => (
                      <tr key={c.id} className="border-t border-line align-top">
                        <td className="px-3 py-2">
                          <Link
                            to="/reklam-merkezi/google-ads/$id"
                            params={{ id: c.id }}
                            className="font-medium text-pine underline-offset-2 hover:underline"
                          >
                            {c.name}
                          </Link>
                          <div className="text-ink-3">
                            {t(`${G}.kinds.${c.kind}`)} · {c.storeName}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={statusTone[c.status]}>{t(`${G}.statuses.${c.status}`)}</Badge>
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {fmt(c.dailyBudgetMinor, c.currency)}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {fmt(c.monthlyMaxMinor, c.currency)}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {fmt(c.monthSpentMinor, c.currency)}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">{c.products}</td>
                        <td className="space-y-1 px-3 py-2">
                          {c.drift.length > 0 && <Badge tone="rose">{t(`${G}.driftBadge`)}</Badge>}
                          {c.pendingApproval > 0 && (
                            <Badge tone="amber">{t(`${G}.pendingBadge`, { count: c.pendingApproval })}</Badge>
                          )}
                          <AlertList alerts={c.alerts} currency={c.currency} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      )}
      {wizard && <CampaignWizard onClose={() => setWizard(false)} />}
      {settings && o && <SettingsDialog current={o.settings} onClose={() => setSettings(false)} />}
    </PageFrame>
  );
}

/** İşlemin kısa açıklaması: "Günlük bütçe 150,00 ₺ → 200,00 ₺" gibi. */
function useOperationText() {
  const t = useT();
  const fmt = useMoney();
  const dates = useDates();
  return (o: Pick<GadsOperation, 'kind' | 'payload' | 'before'>, currency: string) => {
    const p = o.payload;
    const b = (o.before ?? {}) as Record<string, unknown>;
    switch (o.kind) {
      case 'budget':
        return t(`${G}.ops.budgetText`, {
          from: fmt(Number(b.dailyBudgetMinor), currency),
          to: fmt(Number(p.dailyBudgetMinor), currency),
        });
      case 'bidding':
        return t(`${G}.ops.biddingText`, {
          bidding: t(`${G}.biddings.${String(p.bidding)}`),
          target:
            p.targetRoasPercent != null
              ? t(`${G}.roasValue`, { value: Number(p.targetRoasPercent) })
              : p.targetCpaMinor != null
                ? fmt(Number(p.targetCpaMinor), currency)
                : t(`${G}.noTarget`),
        });
      case 'targeting':
        return t(`${G}.ops.targetingText`, {
          countries: (p.countries as string[]).map((c) => t(`${G}.countryNames.${c}`)).join(', '),
          languages: (p.languages as string[]).map((l) => t(`${G}.languages.${l}`)).join(', '),
          start: p.startDate ? dates.day(p.startDate as string) : t(`${G}.startNow`),
          end: p.endDate ? dates.day(p.endDate as string) : t(`${G}.noEnd`),
        });
      case 'rename':
        return t(`${G}.ops.renameText`, { name: String(p.name) });
      case 'apply_recommendation':
        return t(`${G}.ops.recommendationText`, { type: String(p.type) });
      case 'ad_text':
        return t(`${G}.ops.adTextText`, {
          headline: (p.ad as SearchAdValue | undefined)?.headlines[0]?.text ?? '',
        });
      case 'keywords':
      case 'negatives':
        return t(`${G}.ops.keywordsText`, {
          add: Array.isArray(p.add) ? p.add.length : 0,
          remove: Array.isArray(p.remove) ? p.remove.length : 0,
        });
      default:
        // Oluşturma, yayına alma, duraklatma, kaldırma: işlemin adı yeterli (ayrıca açıklama yok).
        return '';
    }
  };
}

function Approvals({ items, loading }: { items: GadsApproval[]; loading: boolean }) {
  const t = useT();
  const fmt = useMoney();
  const { can } = usePermissions();
  const text = useOperationText();
  const [deciding, setDeciding] = useState<{ op: GadsApproval; approve: boolean } | null>(null);
  return (
    <Section
      title={t(`${G}.approvalsTitle`)}
      description={t(`${G}.approvalsLead`)}
      action={<HelpTip k="gads.approvals" />}
    >
      {loading ? (
        <Spinner label={t('common.loading')} />
      ) : !items.length ? (
        <p className="text-sm text-ink-3">{t(`${G}.noApprovals`)}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => (
            <li
              key={a.id}
              className="rounded-md border border-line bg-surface p-3"
              aria-label={a.campaign.name}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="font-medium text-ink">
                    {a.campaign.name} — {t(`${G}.ops.kinds.${a.kind}`)}
                  </p>
                  {text(a, a.campaign.currency) && (
                    <p className="text-sm text-ink-2">{text(a, a.campaign.currency)}</p>
                  )}
                  <p className="text-xs text-ink-3">
                    {t(`${G}.requestedBy`, { name: a.requestedByName ?? t(`${G}.system`) })}
                    {a.note ? ` — ${a.note}` : ''}
                  </p>
                </div>
                {can('marketing.ads.approve') && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="primary" onClick={() => setDeciding({ op: a, approve: true })}>
                      <Check className="h-4 w-4" aria-hidden />
                      {t(`${G}.approve`)}
                    </Button>
                    <Button size="sm" onClick={() => setDeciding({ op: a, approve: false })}>
                      <X className="h-4 w-4" aria-hidden />
                      {t(`${G}.reject`)}
                    </Button>
                    <HelpTip k="gads.decide" />
                  </div>
                )}
              </div>
              {a.textReview && (
                <div className="mt-3 space-y-2">
                  <SearchAdPreview ad={a.textReview.ad} />
                  {a.textReview.required && (
                    <p
                      className={`text-sm ${a.textReview.status === 'approved' ? 'text-moss' : 'text-amber'}`}
                    >
                      {t(`${G}.textReviewStatus.${a.textReview.status}`, { count: a.textReview.findings })}
                    </p>
                  )}
                </div>
              )}
              {(a.kind === 'keywords' || a.kind === 'negatives') && (
                <KeywordChanges add={a.payload.add} remove={a.payload.remove} />
              )}
              {!NO_MONEY.has(a.kind) && (
                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs text-ink-3">{t(`${G}.dailyBudget`)}</dt>
                    <dd className="tabular">{fmt(a.impact.dailyBudgetMinor, a.campaign.currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-3">{t(`${G}.dailyMax`)}</dt>
                    <dd className="tabular">{fmt(a.impact.dailyMaxMinor, a.campaign.currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-3">{t(`${G}.monthlyMax`)}</dt>
                    <dd className="tabular">{fmt(a.impact.monthlyMaxMinor, a.campaign.currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-3">{t(`${G}.projectedAll`)}</dt>
                    <dd className="tabular">
                      {fmt(a.impact.projectedMonthlyMinor, a.campaign.currency)}{' '}
                      {a.impact.capMinor === null ? (
                        <Badge tone="rose">{t(`${G}.capMissing`)}</Badge>
                      ) : !a.impact.capOk ? (
                        <Badge tone="rose">
                          {t(`${G}.capExceeded`, { cap: fmt(a.impact.capMinor, a.campaign.currency) })}
                        </Badge>
                      ) : null}
                    </dd>
                  </div>
                </dl>
              )}
              {a.impact.loosens && <p className="mt-2 text-sm text-amber">{t(`${G}.loosensWarning`)}</p>}
              {a.kind === 'remove' && <p className="mt-2 text-sm text-rose">{t(`${G}.removeWarning`)}</p>}
            </li>
          ))}
        </ul>
      )}
      {deciding && <DecisionDialog {...deciding} onClose={() => setDeciding(null)} />}
    </Section>
  );
}

/** Anahtar kelime / negatif isteğinde eklenen ve çıkarılan kelimeler (onay ekranı). */
function KeywordChanges({ add, remove }: { add: unknown; remove: unknown }) {
  const t = useT();
  const list = (x: unknown) => (Array.isArray(x) ? (x as KeywordValue[]) : []);
  const row = (k: KeywordValue) => `${k.text} (${t(`${G}.search.matchTypes.${k.matchType}`)})`;
  return (
    <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
      {(
        [
          ['added', list(add)],
          ['removed', list(remove)],
        ] as const
      ).map(([k, xs]) => (
        <div key={k}>
          <dt className="text-xs text-ink-3">{t(`${G}.keywordChanges.${k}`, { count: xs.length })}</dt>
          <dd>{xs.length ? xs.map(row).join(', ') : '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

function DecisionDialog({
  op,
  approve,
  onClose,
}: {
  op: GadsApproval;
  approve: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const fmt = useMoney();
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const decide = useApiMutation(
    () =>
      api.post(
        `/v1/admin/google-ads/operations/${op.id}/decision`,
        { approve, note: note.trim() || undefined, version: op.version },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gads'], ['gads-approvals'], ['gads-campaign', op.campaignId]],
      success: approve ? t(`${G}.approved`) : t(`${G}.rejected`),
      onSuccess: onClose,
    },
  );
  const spends = op.kind === 'enable' || op.kind === 'budget' || op.kind === 'apply_recommendation';
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={approve ? t(`${G}.approveTitle`) : t(`${G}.rejectTitle`)}
      description={`${op.campaign.name} — ${t(`${G}.ops.kinds.${op.kind}`)}`}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={approve ? 'primary' : 'danger'}
            loading={decide.isPending}
            onClick={() => decide.mutate(undefined)}
          >
            {approve ? t(`${G}.approveSubmit`) : t(`${G}.rejectSubmit`)}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {approve && spends && (
          <div className="rounded-md border border-line bg-surface-2 p-3 text-sm text-ink">
            <p>{t(`${G}.moneyLine`, { daily: fmt(op.impact.dailyBudgetMinor, op.campaign.currency) })}</p>
            <p>
              {t(`${G}.moneyRule`, {
                dailyMax: fmt(op.impact.dailyMaxMinor, op.campaign.currency),
                monthlyMax: fmt(op.impact.monthlyMaxMinor, op.campaign.currency),
              })}
            </p>
            <p>
              {t(`${G}.moneyProjected`, {
                value: fmt(op.impact.projectedMonthlyMinor, op.campaign.currency),
              })}
            </p>
          </div>
        )}
        {approve && op.kind === 'remove' && <p className="text-sm text-rose">{t(`${G}.removeWarning`)}</p>}
        {approve && <p className="text-sm text-ink-2">{t(`${G}.approveStepUp`)}</p>}
        <Field label={t(`${G}.decisionNote`)} help={<HelpTip k="gads.decision_note" />}>
          {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

function SettingsDialog({ current, onClose }: { current: GadsSettings; onClose: () => void }) {
  const t = useT();
  const { can } = usePermissions();
  const [cap, setCap] = useState(minorToInput(current.monthlyCapMinor));
  const [autoStop, setAutoStop] = useState(current.autoStop);
  const [accessLevel, setAccessLevel] = useState(current.accessLevel);
  const capMinor = toMinor(cap, current.currency);
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/google-ads/settings', {
        monthlyCapMinor: cap.trim() ? capMinor : null,
        currency: current.currency,
        autoStop,
        dailyOperationLimit: current.dailyOperationLimit,
        accessLevel,
      }),
    {
      invalidate: [['gads'], ['gads-approvals'], ['gads-settings']],
      success: t(`${G}.settingsSaved`),
      onSuccess: onClose,
    },
  );
  const editable = can('marketing.ads.approve');
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`${G}.settingsTitle`)}
      description={t(`${G}.settingsLead`)}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {editable && (
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={cap.trim() !== '' && (capMinor === null || capMinor <= 0)}
              onClick={() => save.mutate(undefined)}
            >
              {t('common.save')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t(`${G}.monthlyCapLabel`, { currency: current.currency })}
          help={<HelpTip k="gads.monthly_cap" />}
          description={t(`${G}.monthlyCapHint`)}
          error={fieldError(save.error, 'monthlyCapMinor')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={cap}
              disabled={!editable}
              onChange={(e) => setCap(e.target.value)}
            />
          )}
        </Field>
        <div className="flex items-center gap-2">
          <Switch
            checked={autoStop}
            onCheckedChange={setAutoStop}
            disabled={!editable}
            label={t(`${G}.autoStop`)}
          />
          <HelpTip k="gads.auto_stop" />
        </div>
        <p className="text-sm text-ink-3">{t(`${G}.autoStopHint`)}</p>
        <p className="text-sm text-ink-3">
          {t(`${G}.dailyLimitHint`, { limit: current.dailyOperationLimit })} <HelpTip k="gads.daily_limit" />
        </p>
        <Field
          label={t(`${G}.accessLevel`)}
          help={<HelpTip k="gads.access_level" />}
          description={t(`${G}.accessLevelHint`)}
        >
          {(p) => (
            <Select
              {...p}
              value={accessLevel}
              disabled={!editable}
              onChange={(e) => setAccessLevel(e.target.value as GadsSettings['accessLevel'])}
            >
              {(['explorer', 'basic', 'standard'] as const).map((l) => (
                <option key={l} value={l}>
                  {t(`${G}.accessLevels.${l}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {!editable && <p className="text-sm text-ink-3">{t(`${G}.settingsReadOnly`)}</p>}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------ Sihirbaz

const ALL_STEPS = ['store', 'campaign', 'products', 'ad', 'budget', 'targeting', 'summary'] as const;
type Step = (typeof ALL_STEPS)[number];
/** Arama kampanyasında ürün adımı yerine reklam metni ve anahtar kelimeler (F7-40). */
const stepsFor = (kind: GadsKind): Step[] =>
  ALL_STEPS.filter((s) => (kind === 'search' ? s !== 'products' : s !== 'ad'));

function CampaignWizard({ onClose }: { onClose: () => void }) {
  const t = useT();
  const fmt = useMoney();
  const { stores, storeId: chosen } = useStoreChoice();
  const [step, setStep] = useState<Step>('store');
  const [storeId, setStoreId] = useState(chosen);
  const pre = useQuery({
    queryKey: ['gads-pre', storeId],
    queryFn: () => api.get<GadsPrerequisites>(`/v1/admin/google-ads/prerequisites?storeId=${storeId}`),
    enabled: !!storeId,
    retry: false,
  });
  const store = stores.find((s) => s.id === storeId);
  const currency = pre.data?.currency ?? 'TRY';
  const [kind, setKind] = useState<GadsKind>('pmax');
  const STEPS = stepsFor(kind);
  const [ad, setAd] = useState<SearchAdValue>(() => emptyAd());
  const [keywords, setKeywords] = useState<KeywordValue[]>([]);
  const [negatives, setNegatives] = useState<KeywordValue[]>([]);
  const allowed = (k: GadsKind) => (k === 'search' ? !!pre.data?.searchReady : !!pre.data?.ready);
  const [name, setName] = useState('');
  const [productIds, setProductIds] = useState<string[]>([]);
  const [productTitles, setProductTitles] = useState<Record<string, string>>({});
  const [budget, setBudget] = useState('');
  const [bidding, setBidding] = useState<GadsBidding>('max_conv_value');
  const [roas, setRoas] = useState('');
  const [cpa, setCpa] = useState('');
  const defaultCountry =
    store?.markets
      ?.find((m) => m.isDefault)
      ?.countryCode?.trim()
      .toUpperCase() ?? 'TR';
  const [countries, setCountries] = useState<string[]>([
    COUNTRIES.includes(defaultCountry) ? defaultCountry : 'TR',
  ]);
  const [languages, setLanguages] = useState<string[]>(['tr']);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<{
    rejected: GadsEligibility[];
    submitted: boolean;
    id: string;
  } | null>(null);
  const [idem] = useState(() => new IdempotencyKey());
  const budgetMinor = toMinor(budget, currency);
  const cpaMinor = toMinor(cpa, currency);
  const roasPercent = roas.trim() ? Number(roas.replace(',', '.')) : null;
  const draft = {
    storeId,
    kind,
    name: name.trim(),
    dailyBudgetMinor: budgetMinor ?? 0,
    bidding,
    targetRoasPercent: bidding === 'max_conv_value' && roasPercent !== null ? Math.round(roasPercent) : null,
    targetCpaMinor: bidding === 'max_conversions' && cpa.trim() ? cpaMinor : null,
    countries,
    languages,
    startDate: startDate || null,
    endDate: endDate || null,
    productIds: kind === 'search' ? [] : productIds,
    ...(kind === 'search'
      ? {
          search: {
            ad: cleanAd(ad),
            keywords: keywords.filter((k) => k.text.trim()),
            negatives: negatives.filter((k) => k.text.trim()),
          },
        }
      : {}),
  };
  const create = useApiMutation(
    (submit: boolean) =>
      api.post<{ campaign: GadsCampaign; rejected: GadsEligibility[] }>(
        '/v1/admin/google-ads/campaigns',
        { draft, submit, note: note.trim() || undefined },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gads'], ['gads-approvals']],
      onSuccess: (r, submit) => setResult({ rejected: r.rejected, submitted: submit, id: r.campaign.id }),
    },
  );
  const i = STEPS.indexOf(step);
  const valid: Record<Step, boolean> = {
    store: !!pre.data?.ready || !!pre.data?.searchReady,
    campaign: name.trim().length >= 2 && allowed(kind),
    products: productIds.length > 0,
    ad: adProblems(ad).length === 0 && keywords.some((k) => k.text.trim()),
    budget:
      budgetMinor !== null &&
      budgetMinor > 0 &&
      (roasPercent === null || (Number.isFinite(roasPercent) && roasPercent >= 1)) &&
      (!cpa.trim() || (cpaMinor !== null && cpaMinor > 0)),
    targeting:
      countries.length > 0 && languages.length > 0 && (!startDate || !endDate || endDate >= startDate),
    summary: true,
  };
  const daily = budgetMinor && budgetMinor > 0 ? budgetMinor : 0;
  if (result)
    return (
      <Dialog
        open
        onOpenChange={(v) => !v && onClose()}
        title={result.submitted ? t(`${G}.wizardSubmitted`) : t(`${G}.wizardSaved`)}
        closeLabel={t('common.close')}
        footer={
          <>
            <Link
              to="/reklam-merkezi/google-ads/$id"
              params={{ id: result.id }}
              className={buttonStyles({ variant: 'primary' })}
            >
              {t(`${G}.openCampaign`)}
            </Link>
            <Button onClick={onClose}>{t('common.close')}</Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>{result.submitted ? t(`${G}.wizardSubmittedLead`) : t(`${G}.wizardSavedLead`)}</p>
          {result.rejected.length > 0 && (
            <RejectedList items={result.rejected} title={t(`${G}.rejectedTitle`)} />
          )}
        </div>
      </Dialog>
    );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`${G}.wizardTitle`)}
      description={t(`${G}.wizardStep`, { n: i + 1, total: STEPS.length, name: t(`${G}.steps.${step}`) })}
      closeLabel={t('common.close')}
      footer={
        <>
          {i > 0 && <Button onClick={() => setStep(STEPS[i - 1]!)}>{t(`${G}.back`)}</Button>}
          {step !== 'summary' ? (
            <Button variant="primary" disabled={!valid[step]} onClick={() => setStep(STEPS[i + 1]!)}>
              {t(`${G}.next`)}
            </Button>
          ) : (
            <>
              <Button
                loading={create.isPending && create.variables === false}
                onClick={() => create.mutate(false)}
              >
                {t(`${G}.saveDraft`)}
              </Button>
              <Button
                variant="primary"
                loading={create.isPending && create.variables === true}
                onClick={() => create.mutate(true)}
              >
                {t(`${G}.submitForApproval`)}
              </Button>
              <HelpTip k="gads.submit" />
            </>
          )}
        </>
      }
    >
      <ol className="mb-4 flex flex-wrap gap-2 text-xs" aria-label={t(`${G}.stepsLabel`)}>
        {STEPS.map((s, n) => (
          <li
            key={s}
            aria-current={s === step ? 'step' : undefined}
            className={`rounded-full border px-2 py-0.5 ${s === step ? 'border-pine bg-pine/10 text-pine' : 'border-line text-ink-3'}`}
          >
            {n + 1}. {t(`${G}.steps.${s}`)}
          </li>
        ))}
      </ol>
      {step === 'store' && (
        <div className="space-y-4">
          <Field label={t(`${G}.store`)} required help={<HelpTip k="gads.store" />}>
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {pre.isLoading ? (
            <Spinner label={t(`${G}.checking`)} />
          ) : pre.error ? (
            <p className="text-sm text-rose">{(pre.error as ApiError).title}</p>
          ) : !pre.data?.ready && pre.data?.searchReady ? (
            <div role="status" className="rounded-md border border-amber/40 bg-amber/5 p-3 text-sm">
              <p className="font-medium text-ink">{t(`${G}.searchOnlyReady`)}</p>
              <ul className="mt-1 list-disc space-y-1 ps-5 text-ink-2">
                {pre.data.reasons.map((r) => (
                  <li key={r}>{t(`errors.${r}`)}</li>
                ))}
              </ul>
            </div>
          ) : pre.data?.ready ? (
            <div className="rounded-md border border-line bg-surface-2 p-3 text-sm">
              <p className="font-medium text-moss">{t(`${G}.ready`)}</p>
              <p className="text-ink-2">
                {t(`${G}.readyLead`, {
                  customer: pre.data.customerId ?? '',
                  merchant: pre.data.merchantId ?? '',
                  conversion: pre.data.purchaseConversions.find((c) => c.primary)?.name ?? '',
                })}
              </p>
            </div>
          ) : (
            <div role="alert" className="rounded-md border border-rose/40 bg-rose/5 p-3 text-sm">
              <p className="font-medium text-ink">{t(`${G}.locked`)}</p>
              <ul className="mt-1 list-disc space-y-1 ps-5 text-ink-2">
                {(pre.data?.reasons ?? []).map((r) => (
                  <li key={r}>{t(`errors.${r}`)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {step === 'campaign' && (
        <div className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="flex items-center gap-1 text-sm font-medium text-ink">
              {t(`${G}.kind`)} <HelpTip k="gads.kind" />
            </legend>
            {(['pmax', 'shopping', 'search'] as const).map((k) => (
              <label key={k} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="gads-kind"
                  className="mt-1 accent-[var(--zt-pine)]"
                  checked={kind === k}
                  disabled={!allowed(k)}
                  onChange={() => {
                    setKind(k);
                    if (!BIDDING_BY_KIND[k].includes(bidding)) setBidding(BIDDING_BY_KIND[k][0]!);
                  }}
                />
                <span>
                  <span className="font-medium text-ink">{t(`${G}.kinds.${k}`)}</span>
                  <span className="block text-ink-3">{t(`${G}.kindHints.${k}`)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <Field label={t(`${G}.name`)} required help={<HelpTip k="gads.name" />}>
            {(p) => <Input {...p} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}
          </Field>
        </div>
      )}
      {step === 'ad' && (
        <div className="space-y-6">
          <SearchAdEditor
            value={ad}
            storeId={storeId}
            findings={[]}
            errors={(create.error as ApiError | null) ?? null}
            onChange={(v, suggested) => {
              setAd(v);
              if (suggested?.length && !keywords.length) setKeywords(suggested);
            }}
          />
          <KeywordsEditor
            label={t(`${G}.search.keywords`)}
            lead={t(`${G}.search.keywordsLead`)}
            help="gads.search_keywords"
            value={keywords}
            onChange={setKeywords}
            max={200}
          />
          <KeywordIdeas
            storeId={storeId}
            seeds={keywords.map((k) => k.text).filter(Boolean)}
            url={ad.finalUrl}
            onAdd={(texts) =>
              setKeywords((cur) => [
                ...cur,
                ...texts
                  .filter(
                    (x) => !cur.some((k) => k.text.toLocaleLowerCase('tr') === x.toLocaleLowerCase('tr')),
                  )
                  .map((text) => ({ text, matchType: 'phrase' as const })),
              ])
            }
          />
          <KeywordsEditor
            label={t(`${G}.search.negatives`)}
            lead={t(`${G}.search.negativesLead`)}
            help="gads.search_negatives"
            value={negatives}
            onChange={setNegatives}
            max={200}
          />
        </div>
      )}
      {step === 'products' && (
        <ProductPicker
          selected={productIds}
          titles={productTitles}
          onChange={(ids, titles) => {
            setProductIds(ids);
            setProductTitles((cur) => ({ ...cur, ...titles }));
          }}
        />
      )}
      {step === 'budget' && (
        <div className="space-y-4">
          <Field
            label={t(`${G}.dailyBudgetLabel`, { currency })}
            required
            help={<HelpTip k="gads.daily_budget" />}
            error={
              budget.trim() && (budgetMinor === null || budgetMinor <= 0)
                ? t(`${G}.budgetInvalid`)
                : undefined
            }
          >
            {(p) => (
              <Input {...p} inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} />
            )}
          </Field>
          <p className="rounded-md border border-line bg-surface-2 p-3 text-sm text-ink" aria-live="polite">
            {t(`${G}.moneyRule`, {
              dailyMax: fmt(daily * 2, currency),
              monthlyMax: fmt(monthlyMax(daily), currency),
            })}
          </p>
          <Field label={t(`${G}.bidding`)} required help={<HelpTip k="gads.bidding" />}>
            {(p) => (
              <Select {...p} value={bidding} onChange={(e) => setBidding(e.target.value as GadsBidding)}>
                {BIDDING_BY_KIND[kind].map((b) => (
                  <option key={b} value={b}>
                    {t(`${G}.biddings.${b}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {bidding === 'max_conv_value' && (
            <Field
              label={t(`${G}.targetRoas`)}
              help={<HelpTip k="gads.target_roas" />}
              description={t(`${G}.targetRoasHint`)}
            >
              {(p) => (
                <Input {...p} inputMode="numeric" value={roas} onChange={(e) => setRoas(e.target.value)} />
              )}
            </Field>
          )}
          {bidding === 'max_conversions' && (
            <Field
              label={t(`${G}.targetCpa`, { currency })}
              help={<HelpTip k="gads.target_cpa" />}
              description={t(`${G}.targetCpaHint`)}
            >
              {(p) => (
                <Input {...p} inputMode="decimal" value={cpa} onChange={(e) => setCpa(e.target.value)} />
              )}
            </Field>
          )}
        </div>
      )}
      {step === 'targeting' && (
        <TargetingFields
          countries={countries}
          languages={languages}
          startDate={startDate}
          endDate={endDate}
          onChange={(v) => {
            if (v.countries) setCountries(v.countries);
            if (v.languages) setLanguages(v.languages);
            if (v.startDate !== undefined) setStartDate(v.startDate);
            if (v.endDate !== undefined) setEndDate(v.endDate);
          }}
        />
      )}
      {step === 'summary' && (
        <div className="space-y-3 text-sm">
          <dl className="grid gap-2 sm:grid-cols-2">
            {[
              [t(`${G}.store`), store?.name ?? ''],
              [t(`${G}.kind`), t(`${G}.kinds.${kind}`)],
              [t(`${G}.name`), name],
              kind === 'search'
                ? [
                    t(`${G}.search.summaryAd`),
                    t(`${G}.search.summaryAdValue`, {
                      headlines: cleanAd(ad).headlines.length,
                      descriptions: cleanAd(ad).descriptions.length,
                      keywords: keywords.filter((k) => k.text.trim()).length,
                      negatives: negatives.filter((k) => k.text.trim()).length,
                    }),
                  ]
                : [t(`${G}.productsCount`), String(productIds.length)],
              [t(`${G}.dailyBudget`), fmt(daily, currency)],
              [t(`${G}.monthlyMax`), fmt(monthlyMax(daily), currency)],
              [
                t(`${G}.bidding`),
                `${t(`${G}.biddings.${bidding}`)}${draft.targetRoasPercent ? ` — ${t(`${G}.roasValue`, { value: draft.targetRoasPercent })}` : ''}${draft.targetCpaMinor ? ` — ${fmt(draft.targetCpaMinor, currency)}` : ''}`,
              ],
              [t(`${G}.countries`), countries.map((x) => t(`${G}.countryNames.${x}`)).join(', ')],
              [t(`${G}.languagesLabel`), languages.map((l) => t(`${G}.languages.${l}`)).join(', ')],
              [t(`${G}.dates`), `${startDate || t(`${G}.startNow`)} — ${endDate || t(`${G}.noEnd`)}`],
            ].map(([k, v]) => (
              <div key={k} className="rounded-md border border-line px-3 py-2">
                <dt className="text-xs text-ink-3">{k}</dt>
                <dd className="font-medium text-ink">{v}</dd>
              </div>
            ))}
          </dl>
          {kind === 'search' ? (
            <>
              <SearchAdPreview ad={cleanAd(ad)} />
              <p className="text-ink-2">{t(`${G}.search.summaryReviewNote`)}</p>
            </>
          ) : (
            <p className="text-ink-2">{t(`${G}.assetsNote`)}</p>
          )}
          <p className="text-ink-2">{t(`${G}.pausedNote`)}</p>
          <Field label={t(`${G}.requestNote`)} help={<HelpTip k="gads.request_note" />}>
            {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function RejectedList({ items, title }: { items: GadsEligibility[]; title: string }) {
  const t = useT();
  return (
    <div role="status" className="rounded-md border border-amber/40 bg-amber/5 p-3">
      <p className="font-medium text-ink">{title}</p>
      <ul className="mt-1 space-y-1 text-ink-2">
        {items.map((r) => (
          <li key={r.productId}>
            <span className="font-medium">{r.title || r.productId}</span>:{' '}
            {r.reasons.map((x) => t(`${G}.reasons.${x}`)).join('; ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProductPicker({
  selected,
  titles,
  onChange,
}: {
  selected: string[];
  titles: Record<string, string>;
  onChange: (ids: string[], titles: Record<string, string>) => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['gads-products', q],
    queryFn: () =>
      api.get<ProductPage>(
        `/v1/admin/products?status=active&limit=50${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`,
      ),
  });
  const toggle = (id: string, title: string, on: boolean) =>
    onChange(on ? [...new Set([...selected, id])] : selected.filter((x) => x !== id), { [id]: title });
  return (
    <div className="space-y-3">
      <Field
        label={t(`${G}.productSearch`)}
        help={<HelpTip k="gads.products" />}
        description={t(`${G}.productsHint`)}
      >
        {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
      </Field>
      <p className="text-sm text-ink-2" role="status">
        {t(`${G}.selectedCount`, { count: selected.length })}
      </p>
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {selected.map((id) => (
            <li key={id}>
              <Badge tone="pine">{titles[id] ?? id}</Badge>
            </li>
          ))}
        </ul>
      )}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <ul className="max-h-72 divide-y divide-line overflow-y-auto rounded-md border border-line">
          {(list.data?.items ?? []).map((p) => (
            <li key={p.id} className="px-3 py-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-[var(--zt-pine)]"
                  checked={selected.includes(p.id)}
                  onChange={(e) => toggle(p.id, p.title, e.target.checked)}
                />
                <span className="font-medium text-ink">{p.title}</span>
                <span className="text-ink-3">{p.firstSku}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TargetingFields(props: {
  countries: string[];
  languages: string[];
  startDate: string;
  endDate: string;
  onChange: (
    v: Partial<{ countries: string[]; languages: string[]; startDate: string; endDate: string }>,
  ) => void;
}) {
  const t = useT();
  const toggle = (list: string[], v: string, on: boolean) =>
    on ? [...new Set([...list, v])] : list.filter((x) => x !== v);
  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="flex items-center gap-1 text-sm font-medium text-ink">
          {t(`${G}.countries`)} <HelpTip k="gads.countries" />
        </legend>
        <div className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-4">
          {COUNTRIES.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-[var(--zt-pine)]"
                checked={props.countries.includes(c)}
                onChange={(e) => props.onChange({ countries: toggle(props.countries, c, e.target.checked) })}
              />
              {t(`${G}.countryNames.${c}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="flex items-center gap-1 text-sm font-medium text-ink">
          {t(`${G}.languagesLabel`)} <HelpTip k="gads.languages" />
        </legend>
        <div className="mt-2 flex flex-wrap gap-3">
          {LANGUAGES.map((l) => (
            <label key={l} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-[var(--zt-pine)]"
                checked={props.languages.includes(l)}
                onChange={(e) => props.onChange({ languages: toggle(props.languages, l, e.target.checked) })}
              />
              {t(`${G}.languages.${l}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={t(`${G}.startDate`)}
          help={<HelpTip k="gads.dates" />}
          description={t(`${G}.startHint`)}
        >
          {(p) => (
            <Input
              {...p}
              type="date"
              value={props.startDate}
              onChange={(e) => props.onChange({ startDate: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t(`${G}.endDate`)}
          description={t(`${G}.endHint`)}
          error={
            props.startDate && props.endDate && props.endDate < props.startDate
              ? t('errors.google_ads_end_before_start')
              : undefined
          }
        >
          {(p) => (
            <Input
              {...p}
              type="date"
              value={props.endDate}
              onChange={(e) => props.onChange({ endDate: e.target.value })}
            />
          )}
        </Field>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Kampanya ayrıntısı

export function GoogleAdsCampaignPage({ id }: { id: string }) {
  const t = useT();
  const fmt = useMoney();
  const dates = useDates();
  const errorText = useErrorText();
  const opText = useOperationText();
  const { can } = usePermissions();
  /** Google'ın birincil durumu (ELIGIBLE, LIMITED…) → metin; bilinmeyen değer olduğu gibi. */
  const primaryLabel = (v: string) => {
    const s = t(`${G}.primaryStatuses.${v}`);
    return s === `${G}.primaryStatuses.${v}` ? v : s;
  };
  const detail = useQuery({
    queryKey: ['gads-campaign', id],
    queryFn: () => api.get<GadsCampaignDetail>(`/v1/admin/google-ads/campaigns/${id}`),
  });
  const [op, setOp] = useState<GadsOperationKind | null>(null);
  const [adding, setAdding] = useState(false);
  const accept = useApiMutation(
    () =>
      api.post(`/v1/admin/google-ads/campaigns/${id}/accept-google`, {
        version: detail.data?.campaign.version,
      }),
    { invalidate: [['gads-campaign', id], ['gads']], success: t(`${G}.driftAccepted`) },
  );
  const cancel = useApiMutation(
    (opId: string) => api.post(`/v1/admin/google-ads/operations/${opId}/cancel`),
    {
      invalidate: [['gads-campaign', id], ['gads-approvals'], ['gads']],
      success: t(`${G}.cancelled`),
    },
  );
  const remove = useApiMutation(
    (productId: string) =>
      api.post(`/v1/admin/google-ads/campaigns/${id}/products/remove`, { productIds: [productId] }),
    { invalidate: [['gads-campaign', id], ['gads']], success: t(`${G}.productRemoved`) },
  );
  const deleteDraft = useApiMutation(() => api.del(`/v1/admin/google-ads/campaigns/${id}`), {
    invalidate: [['gads']],
    success: t(`${G}.draftDeleted`),
    onSuccess: () => window.history.back(),
  });
  if (detail.isLoading || !detail.data)
    return (
      <PageFrame helpKey="page.google_ads_campaign">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const { campaign: c, products, operations } = detail.data;
  const manage = can('marketing.ads.manage') && c.status !== 'removed';
  const open = operations.some((o) => ['pending_approval', 'queued', 'applying'].includes(o.status));
  const actions: GadsOperationKind[] = !c.created
    ? ['create']
    : [
        ...(c.status === 'paused' ? (['enable'] as const) : []),
        ...(c.status === 'enabled' ? (['pause'] as const) : []),
        'budget',
        'bidding',
        'targeting',
        'rename',
        'remove',
      ];
  return (
    <PageFrame
      helpKey="page.google_ads_campaign"
      title={c.name}
      actions={
        <Link to="/reklam-merkezi/google-ads" className={buttonStyles({ variant: 'secondary' })}>
          {t(`${G}.backToList`)}
        </Link>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={statusTone[c.status]}>{t(`${G}.statuses.${c.status}`)}</Badge>
          <span className="text-sm text-ink-2">
            {t(`${G}.kinds.${c.kind}`)}
            {c.googleId ? ` · ${t(`${G}.googleId`, { id: c.googleId })}` : ''} ·{' '}
            {t(`${G}.labelValue`, { label: c.label })}
          </span>
          <HelpTip k="gads.label" />
        </div>
        {c.google?.primaryStatus && (
          <p className="text-sm text-ink-2">
            {t(`${G}.primaryStatus`, { status: primaryLabel(c.google.primaryStatus) })}
            {c.google.primaryStatusReasons.length
              ? ` (${c.google.primaryStatusReasons.join(', ')})`
              : ''} · {t(`${G}.readAt`, { at: dates.at(c.google.readAt) })}
          </p>
        )}
        {c.drift.length > 0 && (
          <div role="alert" className="rounded-md border border-rose/40 bg-rose/5 p-3 text-sm">
            <p className="font-medium text-ink">{t(`${G}.driftTitle`)}</p>
            <p className="text-ink-2">{t(`${G}.driftLead`)}</p>
            <ul className="mt-2 space-y-1">
              {c.drift.map((d) => (
                <li key={d.field}>
                  {t(`${G}.driftFields.${d.field}`)}:{' '}
                  {t(`${G}.driftValues`, { expected: d.expected ?? '—', google: d.google ?? '—' })}
                </li>
              ))}
            </ul>
            {manage && (
              <div className="mt-2 flex items-center gap-1">
                <Button size="sm" loading={accept.isPending} onClick={() => accept.mutate(undefined)}>
                  {t(`${G}.acceptGoogle`)}
                </Button>
                <HelpTip k="gads.accept_google" />
              </div>
            )}
          </div>
        )}
        <dl className="grid gap-3 sm:grid-cols-4">
          {[
            [t(`${G}.dailyBudget`), fmt(c.dailyBudgetMinor, c.currency)],
            [t(`${G}.monthlyMax`), fmt(c.monthlyMaxMinor, c.currency)],
            [
              t(`${G}.bidding`),
              `${t(`${G}.biddings.${c.bidding}`)}${c.targetRoasPercent ? ` — ${t(`${G}.roasValue`, { value: c.targetRoasPercent })}` : ''}${c.targetCpaMinor ? ` — ${fmt(c.targetCpaMinor, c.currency)}` : ''}`,
            ],
            [t(`${G}.goal`), c.goalApplied ? t(`${G}.goalApplied`) : t(`${G}.goalPending`)],
            [t(`${G}.countries`), c.countries.map((x) => t(`${G}.countryNames.${x}`)).join(', ')],
            [t(`${G}.languagesLabel`), c.languages.map((l) => t(`${G}.languages.${l}`)).join(', ')],
            [
              t(`${G}.dates`),
              `${c.startDate ? dates.day(c.startDate) : t(`${G}.startNow`)} — ${c.endDate ? dates.day(c.endDate) : t(`${G}.noEnd`)}`,
            ],
            [t(`${G}.accounts`), c.merchantId ? `${c.customerId} / ${c.merchantId}` : c.customerId],
          ].map(([k, v]) => (
            <div key={k} className="rounded-md border border-line bg-surface px-3 py-2">
              <dt className="text-xs text-ink-3">{k}</dt>
              <dd className="mt-0.5 text-sm font-medium text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        {manage && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t(`${G}.actions`)}>
            {actions.map((k) => (
              <Button
                key={k}
                size="sm"
                variant={
                  k === 'remove' ? 'danger' : k === 'create' || k === 'enable' ? 'primary' : 'secondary'
                }
                disabled={open && k !== 'pause'}
                onClick={() => setOp(k)}
              >
                {t(`${G}.ops.actions.${k}`)}
              </Button>
            ))}
            {c.status === 'draft' && !c.created && (
              <ConfirmButton
                message={t(`${G}.deleteDraftConfirm`)}
                onConfirm={() => deleteDraft.mutateAsync(undefined)}
                disabled={open}
              >
                {t(`${G}.deleteDraft`)}
              </ConfirmButton>
            )}
            <HelpTip k="gads.operations" />
            {open && <span className="text-sm text-ink-3">{t(`${G}.openNote`)}</span>}
          </div>
        )}

        {c.kind === 'search' && detail.data.search && (
          <SearchSection campaign={c} search={detail.data.search} manage={manage} busy={open} />
        )}
        {c.kind !== 'search' && (
          <Section
            title={t(`${G}.productsTitle`)}
            description={t(`${G}.productsLead`)}
            action={
              <div className="flex items-center gap-1">
                {manage && (
                  <Button size="sm" onClick={() => setAdding(true)}>
                    <Plus className="h-4 w-4" aria-hidden />
                    {t(`${G}.addProducts`)}
                  </Button>
                )}
                <HelpTip k="gads.product_states" />
              </div>
            }
          >
            {!products.length ? (
              <p className="text-sm text-ink-3">{t(`${G}.noProducts`)}</p>
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line">
                {products.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
                    <div>
                      <Link
                        to="/urunler/$id"
                        params={{ id: p.productId }}
                        className="font-medium text-pine underline-offset-2 hover:underline"
                      >
                        {p.title}
                      </Link>
                      <div className="mt-0.5">
                        <Badge tone={productTone[p.state]}>{t(`${G}.productStates.${p.state}`)}</Badge>
                      </div>
                      {p.issues.length > 0 && (
                        <ul className="mt-1 list-disc ps-5 text-sm text-ink-2">
                          {p.issues.map((i) => (
                            <li key={i.code}>{i.description || i.code}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {manage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={remove.isPending && remove.variables === p.productId}
                        onClick={() => remove.mutate(p.productId)}
                      >
                        {t(`${G}.removeProduct`)}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        <Section title={t(`${G}.operationsTitle`)} action={<HelpTip k="gads.operation_history" />}>
          {!operations.length ? (
            <p className="text-sm text-ink-3">{t(`${G}.noOperations`)}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full min-w-[40rem] text-sm" aria-label={t(`${G}.operationsTitle`)}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t(`${G}.operation`)}</th>
                    <th className="px-3 py-2 text-start font-medium">{t(`${G}.status`)}</th>
                    <th className="px-3 py-2 text-start font-medium">{t(`${G}.who`)}</th>
                    <th className="px-3 py-2 text-start font-medium">{t(`${G}.when`)}</th>
                    <th className="px-3 py-2 text-end font-medium">
                      <span className="sr-only">{t('common.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {operations.map((o) => (
                    <tr key={o.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium text-ink">{t(`${G}.ops.kinds.${o.kind}`)}</div>
                        {opText(o, c.currency) && <div className="text-ink-3">{opText(o, c.currency)}</div>}
                        {o.origin !== 'user' && (
                          <div className="text-xs text-ink-3">{t(`${G}.origins.${o.origin}`)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={opTone[o.status]}>{t(`${G}.opStatuses.${o.status}`)}</Badge>
                        {o.errorKey && (
                          <p className="mt-1 text-xs text-rose">
                            {errorText(o.errorKey)}
                            {Array.isArray(o.errorDetail?.policyTopics) &&
                            (o.errorDetail.policyTopics as string[]).length > 0
                              ? ` (${(o.errorDetail.policyTopics as string[]).join(', ')})`
                              : ''}
                          </p>
                        )}
                        {o.status === 'queued' && o.nextAttemptAt && (
                          <p className="mt-1 text-xs text-ink-3">
                            {t(`${G}.nextAttempt`, { at: dates.at(o.nextAttemptAt) })}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink-2">
                        {o.requestedByName ?? t(`${G}.system`)}
                        {o.decidedByName ? ` → ${o.decidedByName}` : ''}
                      </td>
                      <td className="px-3 py-2 text-ink-2">
                        {dates.at(o.appliedAt ?? o.decidedAt ?? o.createdAt)}
                      </td>
                      <td className="px-3 py-2 text-end">
                        {['pending_approval', 'queued'].includes(o.status) && can('marketing.ads.manage') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={cancel.isPending && cancel.variables === o.id}
                            onClick={() => cancel.mutate(o.id)}
                          >
                            {t(`${G}.cancel`)}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {c.created && <GoogleReadouts campaign={c} manage={manage} />}
      </div>
      {op && <OperationDialog campaign={c} kind={op} onClose={() => setOp(null)} />}
      {adding && <AddProductsDialog campaignId={c.id} onClose={() => setAdding(false)} />}
    </PageFrame>
  );
}

function GoogleReadouts({ campaign: c, manage }: { campaign: GadsCampaign; manage: boolean }) {
  const t = useT();
  const fmt = useMoney();
  const dates = useDates();
  const [showRecs, setShowRecs] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const recs = useQuery({
    queryKey: ['gads-recs', c.id],
    queryFn: () => api.get<GadsRecommendation[]>(`/v1/admin/google-ads/campaigns/${c.id}/recommendations`),
    enabled: showRecs,
    retry: false,
  });
  const history = useQuery({
    queryKey: ['gads-history', c.id],
    queryFn: () => api.get<GadsChangeEvent[]>(`/v1/admin/google-ads/campaigns/${c.id}/history`),
    enabled: showHistory,
    retry: false,
  });
  const [idem] = useState(() => new IdempotencyKey());
  const recType = (type: string) => {
    const s = t(`${G}.recommendationTypes.${type}`);
    return s === `${G}.recommendationTypes.${type}` ? type : s;
  };
  const request = useApiMutation(
    (resourceName: string) =>
      api.post(
        `/v1/admin/google-ads/campaigns/${c.id}/recommendations`,
        { resourceName },
        { idempotencyKey: idem },
      ),
    { invalidate: [['gads-campaign', c.id], ['gads-approvals']], success: t(`${G}.recommendationRequested`) },
  );
  return (
    <>
      <Section
        title={t(`${G}.recommendationsTitle`)}
        description={t(`${G}.recommendationsLead`)}
        action={
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => (showRecs ? void recs.refetch() : setShowRecs(true))}>
              {t(`${G}.loadRecommendations`)}
            </Button>
            <HelpTip k="gads.recommendations" />
          </div>
        }
      >
        {!showRecs ? null : recs.isLoading ? (
          <Spinner label={t('common.loading')} />
        ) : recs.error ? (
          <p className="text-sm text-rose">{(recs.error as ApiError).title}</p>
        ) : !recs.data?.length ? (
          <p className="text-sm text-ink-3">{t(`${G}.noRecommendations`)}</p>
        ) : (
          <ul className="space-y-2">
            {recs.data.map((r) => (
              <li
                key={r.resourceName}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-medium text-ink">{recType(r.type)}</span>
                  {r.recommendedBudgetMinor !== null && (
                    <span className="block text-ink-2">
                      {t(`${G}.recommendedBudget`, {
                        daily: fmt(r.recommendedBudgetMinor, c.currency),
                        monthly: fmt(r.recommendedMonthlyMaxMinor, c.currency),
                      })}
                    </span>
                  )}
                </span>
                {manage && (
                  <Button
                    size="sm"
                    loading={request.isPending && request.variables === r.resourceName}
                    onClick={() => request.mutate(r.resourceName)}
                  >
                    {t(`${G}.requestRecommendation`)}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section
        title={t(`${G}.historyTitle`)}
        description={t(`${G}.historyLead`)}
        action={
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => (showHistory ? void history.refetch() : setShowHistory(true))}>
              {t(`${G}.loadHistory`)}
            </Button>
            <HelpTip k="gads.change_history" />
          </div>
        }
      >
        {!showHistory ? null : history.isLoading ? (
          <Spinner label={t('common.loading')} />
        ) : history.error ? (
          <p className="text-sm text-rose">{(history.error as ApiError).title}</p>
        ) : !history.data?.length ? (
          <p className="text-sm text-ink-3">{t(`${G}.noHistory`)}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line text-sm">
            {history.data.map((e, n) => (
              <li key={`${e.at}-${n}`} className="px-3 py-2">
                <span className="font-medium text-ink">{e.at}</span> — {e.resourceType} {e.operation}
                {e.changedFields ? ` (${e.changedFields})` : ''}
                <span className="block text-ink-3">
                  {t(`${G}.historyBy`, { client: e.clientType, user: e.userEmail ?? '—' })}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-ink-3">
          {t(`${G}.historyNote`, { at: dates.at(new Date().toISOString()) })}
        </p>
      </Section>
    </>
  );
}

function OperationDialog({
  campaign: c,
  kind,
  onClose,
}: {
  campaign: GadsCampaign;
  kind: GadsOperationKind;
  onClose: () => void;
}) {
  const t = useT();
  const fmt = useMoney();
  const [budget, setBudget] = useState(minorToInput(c.dailyBudgetMinor));
  const [bidding, setBidding] = useState<GadsBidding>(c.bidding);
  const [roas, setRoas] = useState(c.targetRoasPercent ? String(c.targetRoasPercent) : '');
  const [cpa, setCpa] = useState(minorToInput(c.targetCpaMinor));
  const [countries, setCountries] = useState(c.countries);
  const [languages, setLanguages] = useState(c.languages);
  const [startDate, setStartDate] = useState(c.startDate ?? '');
  const [endDate, setEndDate] = useState(c.endDate ?? '');
  const [name, setName] = useState(c.name);
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [idem] = useState(() => new IdempotencyKey());
  const budgetMinor = toMinor(budget, c.currency);
  const cpaMinor = toMinor(cpa, c.currency);
  const body = useMemo(() => {
    const base = { note: note.trim() || undefined };
    switch (kind) {
      case 'budget':
        return { kind, dailyBudgetMinor: budgetMinor ?? 0, ...base };
      case 'bidding':
        return {
          kind,
          bidding,
          targetRoasPercent:
            bidding === 'max_conv_value' && roas.trim() ? Math.round(Number(roas.replace(',', '.'))) : null,
          targetCpaMinor: bidding === 'max_conversions' && cpa.trim() ? cpaMinor : null,
          ...base,
        };
      case 'targeting':
        return {
          kind,
          countries,
          languages,
          startDate: startDate || null,
          endDate: endDate || null,
          ...base,
        };
      case 'rename':
        return { kind, name: name.trim(), ...base };
      case 'remove':
        return { kind, confirmIrreversible: true as const, ...base };
      default:
        return { kind, ...base };
    }
  }, [kind, budgetMinor, bidding, roas, cpa, cpaMinor, countries, languages, startDate, endDate, name, note]);
  // Onay matrisi (sunucuyla aynı): yalnızca bunlar onaysız; onaysızda "uygula" onay kutusu gerekir.
  const widens =
    countries.some((x) => !c.countries.includes(x)) ||
    languages.some((x) => !c.languages.includes(x)) ||
    (!!c.endDate && (!endDate || endDate > c.endDate)) ||
    (!!c.startDate && (!startDate || startDate < c.startDate));
  const noApproval =
    kind === 'pause' ||
    kind === 'rename' ||
    (kind === 'budget' && budgetMinor !== null && budgetMinor < c.dailyBudgetMinor) ||
    (kind === 'targeting' && !widens);
  const send = useApiMutation(
    () =>
      api.post(
        `/v1/admin/google-ads/campaigns/${c.id}/operations`,
        { ...body, ...(noApproval ? { confirm } : {}) },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gads-campaign', c.id], ['gads'], ['gads-approvals']],
      success: noApproval ? t(`${G}.queued`) : t(`${G}.sentForApproval`),
      onSuccess: onClose,
    },
  );
  const invalid =
    (kind === 'budget' && (budgetMinor === null || budgetMinor <= 0 || budgetMinor === c.dailyBudgetMinor)) ||
    (kind === 'rename' && name.trim().length < 2) ||
    (kind === 'targeting' && (!countries.length || !languages.length)) ||
    (noApproval && !confirm);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`${G}.ops.actions.${kind}`)}
      description={noApproval ? t(`${G}.noApprovalLead`) : t(`${G}.approvalLead`)}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={kind === 'remove' ? 'danger' : 'primary'}
            loading={send.isPending}
            disabled={invalid}
            onClick={() => send.mutate(undefined)}
          >
            {noApproval ? t(`${G}.applyNow`) : t(`${G}.sendForApproval`)}
          </Button>
          <HelpTip k="gads.send_operation" />
        </>
      }
    >
      <div className="space-y-4">
        {kind === 'budget' && (
          <>
            <Field
              label={t(`${G}.dailyBudgetLabel`, { currency: c.currency })}
              required
              help={<HelpTip k="gads.daily_budget" />}
              error={fieldError(send.error, 'dailyBudgetMinor')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                />
              )}
            </Field>
            <p className="rounded-md border border-line bg-surface-2 p-3 text-sm" aria-live="polite">
              {t(`${G}.moneyRule`, {
                dailyMax: fmt((budgetMinor ?? 0) * 2, c.currency),
                monthlyMax: fmt(monthlyMax(budgetMinor ?? 0), c.currency),
              })}
            </p>
          </>
        )}
        {kind === 'bidding' && (
          <>
            <Field label={t(`${G}.bidding`)} required help={<HelpTip k="gads.bidding" />}>
              {(p) => (
                <Select {...p} value={bidding} onChange={(e) => setBidding(e.target.value as GadsBidding)}>
                  {BIDDING_BY_KIND[c.kind].map((b) => (
                    <option key={b} value={b}>
                      {t(`${G}.biddings.${b}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {bidding === 'max_conv_value' && (
              <Field
                label={t(`${G}.targetRoas`)}
                help={<HelpTip k="gads.target_roas" />}
                description={t(`${G}.targetRoasHint`)}
              >
                {(p) => (
                  <Input {...p} inputMode="numeric" value={roas} onChange={(e) => setRoas(e.target.value)} />
                )}
              </Field>
            )}
            {bidding === 'max_conversions' && (
              <Field
                label={t(`${G}.targetCpa`, { currency: c.currency })}
                help={<HelpTip k="gads.target_cpa" />}
              >
                {(p) => (
                  <Input {...p} inputMode="decimal" value={cpa} onChange={(e) => setCpa(e.target.value)} />
                )}
              </Field>
            )}
          </>
        )}
        {kind === 'targeting' && (
          <TargetingFields
            countries={countries}
            languages={languages}
            startDate={startDate}
            endDate={endDate}
            onChange={(v) => {
              if (v.countries) setCountries(v.countries);
              if (v.languages) setLanguages(v.languages);
              if (v.startDate !== undefined) setStartDate(v.startDate);
              if (v.endDate !== undefined) setEndDate(v.endDate);
            }}
          />
        )}
        {kind === 'rename' && (
          <Field label={t(`${G}.name`)} required help={<HelpTip k="gads.name" />}>
            {(p) => <Input {...p} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}
          </Field>
        )}
        {kind === 'enable' && (
          <p className="text-sm text-ink-2">
            {t(`${G}.enableLead`, {
              daily: fmt(c.dailyBudgetMinor, c.currency),
              monthly: fmt(c.monthlyMaxMinor, c.currency),
            })}
          </p>
        )}
        {kind === 'create' && <p className="text-sm text-ink-2">{t(`${G}.pausedNote`)}</p>}
        {kind === 'remove' && <p className="text-sm text-rose">{t(`${G}.removeWarning`)}</p>}
        <Field label={t(`${G}.requestNote`)} help={<HelpTip k="gads.request_note" />}>
          {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        {noApproval && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-[var(--zt-pine)]"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
            />
            <span>{t(`${G}.confirmApply`)}</span>
          </label>
        )}
      </div>
    </Dialog>
  );
}

function AddProductsDialog({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const t = useT();
  const [ids, setIds] = useState<string[]>([]);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [rejected, setRejected] = useState<GadsEligibility[] | null>(null);
  const [idem] = useState(() => new IdempotencyKey());
  const add = useApiMutation(
    () =>
      api.post<{ added: string[]; rejected: GadsEligibility[] }>(
        `/v1/admin/google-ads/campaigns/${campaignId}/products`,
        { productIds: ids },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gads-campaign', campaignId], ['gads']],
      success: (r) => t(`${G}.productsAdded`, { count: r.added.length }),
      onSuccess: (r) => (r.rejected.length ? setRejected(r.rejected) : onClose()),
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`${G}.addProducts`)}
      description={t(`${G}.addProductsLead`)}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{rejected ? t('common.close') : t('common.cancel')}</Button>
          {!rejected && (
            <Button
              variant="primary"
              loading={add.isPending}
              disabled={!ids.length}
              onClick={() => add.mutate(undefined)}
            >
              {t(`${G}.addSelected`, { count: ids.length })}
            </Button>
          )}
        </>
      }
    >
      {rejected ? (
        <RejectedList items={rejected} title={t(`${G}.rejectedTitle`)} />
      ) : (
        <ProductPicker
          selected={ids}
          titles={titles}
          onChange={(next, more) => {
            setIds(next);
            setTitles((cur) => ({ ...cur, ...more }));
          }}
        />
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------ Ürün düzenleme kutusu ve toplu işlem

/** Ürün düzenleme sayfasındaki "Google reklamları" kutusu (F7-38). */
export function ProductGoogleAds({ productId }: { productId: string }) {
  const t = useT();
  const dates = useDates();
  const { can } = usePermissions();
  const box = useQuery({
    queryKey: ['gads-product', productId],
    queryFn: () => api.get<GadsProductBox>(`/v1/admin/products/${productId}/google-ads`),
    retry: false,
  });
  const [campaignId, setCampaignId] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const add = useApiMutation(
    () =>
      api.post<{ added: string[]; rejected: GadsEligibility[] }>(
        `/v1/admin/google-ads/campaigns/${campaignId}/products`,
        { productIds: [productId] },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['gads-product', productId]],
      success: (r) => (r.added.length ? t(`${G}.productAdded`) : t(`${G}.productNotAdded`)),
    },
  );
  const remove = useApiMutation(
    (cid: string) =>
      api.post(`/v1/admin/google-ads/campaigns/${cid}/products/remove`, { productIds: [productId] }),
    { invalidate: [['gads-product', productId]], success: t(`${G}.productRemoved`) },
  );
  if (box.isLoading) return <Spinner label={t('common.loading')} />;
  if (!box.data) return null;
  const { membership: m, eligibility: e, campaigns } = box.data;
  const manage = can('marketing.ads.manage');
  const choices = campaigns.filter((x) => x.status !== 'removed');
  return (
    <div className="space-y-3 text-sm">
      {m ? (
        <div className="space-y-1">
          <p>
            {t(`${G}.inCampaign`)}{' '}
            <Link
              to="/reklam-merkezi/google-ads/$id"
              params={{ id: m.campaignId }}
              className="font-medium text-pine underline-offset-2 hover:underline"
            >
              {m.campaignName}
            </Link>{' '}
            <Badge tone={statusTone[m.campaignStatus]}>{t(`${G}.statuses.${m.campaignStatus}`)}</Badge>
          </p>
          <p>
            <Badge tone={productTone[m.state]}>{t(`${G}.productStates.${m.state}`)}</Badge>{' '}
            <span className="text-ink-3">
              {m.checkedAt ? t(`${G}.checkedAt`, { at: dates.at(m.checkedAt) }) : ''}
            </span>
          </p>
          <p className="text-ink-3">{t(`${G}.labelsNote`, { label: m.label })}</p>
          {m.issues.length > 0 && (
            <ul className="list-disc ps-5 text-ink-2">
              {m.issues.map((i) => (
                <li key={i.code}>{i.description || i.code}</li>
              ))}
            </ul>
          )}
          {manage && m.campaignStatus !== 'removed' && (
            <Button
              size="sm"
              variant="ghost"
              loading={remove.isPending}
              onClick={() => remove.mutate(m.campaignId)}
            >
              {t(`${G}.removeFromCampaign`)}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-ink-2">{t(`${G}.notInCampaign`)}</p>
      )}
      {e && !e.eligible && !m && (
        <div className="rounded-md border border-amber/40 bg-amber/5 p-3">
          <p className="font-medium text-ink">{t(`${G}.notEligible`)}</p>
          <ul className="mt-1 list-disc ps-5 text-ink-2">
            {e.reasons.map((r) => (
              <li key={r}>{t(`${G}.reasons.${r}`)}</li>
            ))}
          </ul>
        </div>
      )}
      {e?.warnings.includes('out_of_stock') && <p className="text-amber">{t(`${G}.reasons.out_of_stock`)}</p>}
      {!m && manage && choices.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t(`${G}.chooseCampaign`)} help={<HelpTip k="gads.product_box" />}>
            {(p) => (
              <Select {...p} value={campaignId} onChange={(ev) => setCampaignId(ev.target.value)}>
                <option value="">{t(`${G}.chooseCampaignPlaceholder`)}</option>
                {choices.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} ({t(`${G}.statuses.${x.status}`)})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            size="sm"
            disabled={!campaignId || (e ? !e.eligible : false)}
            loading={add.isPending}
            onClick={() => add.mutate(undefined)}
          >
            {t(`${G}.addToCampaign`)}
          </Button>
        </div>
      )}
      {!choices.length && <p className="text-ink-3">{t(`${G}.noCampaignsYet`)}</p>}
      {add.data && add.data.rejected.length > 0 && (
        <RejectedList items={add.data.rejected} title={t(`${G}.rejectedTitle`)} />
      )}
    </div>
  );
}

/** Ürün listesinde seçili ürünler için "Google kampanyasına ekle / çıkar" (F7-38). */
export function GoogleAdsBulk({ productIds, onDone }: { productIds: string[]; onDone: () => void }) {
  const t = useT();
  const camps = useQuery({
    queryKey: ['gads'],
    queryFn: () => api.get<GadsOverview>('/v1/admin/google-ads/campaigns'),
  });
  const [campaignId, setCampaignId] = useState('');
  const [rejected, setRejected] = useState<GadsEligibility[] | null>(null);
  const [idem] = useState(() => new IdempotencyKey());
  const add = useApiMutation(
    () =>
      api.post<{ added: string[]; rejected: GadsEligibility[] }>(
        `/v1/admin/google-ads/campaigns/${campaignId}/products`,
        { productIds },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['products'], ['gads']],
      success: (r) => t(`${G}.productsAdded`, { count: r.added.length }),
      onSuccess: (r) => {
        setRejected(r.rejected.length ? r.rejected : null);
        if (!r.rejected.length) onDone();
      },
    },
  );
  const remove = useApiMutation(
    () =>
      api.post<{ removed: string[] }>(`/v1/admin/google-ads/campaigns/${campaignId}/products/remove`, {
        productIds,
      }),
    {
      invalidate: [['products'], ['gads']],
      success: (r) => t(`${G}.productsRemoved`, { count: r.removed.length }),
      onSuccess: onDone,
    },
  );
  // Arama kampanyasına ürün eklenmez (F7-40).
  const choices = (camps.data?.campaigns ?? []).filter((c) => c.status !== 'removed' && c.kind !== 'search');
  if (!choices.length) return null;
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-line bg-surface px-3 py-2">
      <Field label={t(`${G}.bulkCampaign`)} help={<HelpTip k="gads.bulk" />}>
        {(p) => (
          <Select {...p} value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">{t(`${G}.chooseCampaignPlaceholder`)}</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Button size="sm" disabled={!campaignId} loading={add.isPending} onClick={() => add.mutate(undefined)}>
        {t(`${G}.bulkAdd`, { count: productIds.length })}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={!campaignId}
        loading={remove.isPending}
        onClick={() => remove.mutate(undefined)}
      >
        {t(`${G}.bulkRemove`)}
      </Button>
      {rejected && <RejectedList items={rejected} title={t(`${G}.rejectedTitle`)} />}
    </div>
  );
}

/** Ürün listesi süzgecinin seçenekleri (F7-38). */
export function useGoogleAdsFilterOptions(enabled: boolean) {
  const camps = useQuery({
    queryKey: ['gads'],
    queryFn: () => api.get<GadsOverview>('/v1/admin/google-ads/campaigns'),
    enabled,
  });
  return (camps.data?.campaigns ?? [])
    .filter((c) => c.status !== 'removed' && c.kind !== 'search')
    .map((c) => ({ id: c.id, name: c.name }));
}
